import React, { useEffect, useState } from "react";
import { MapLinkPaste } from "@/components/MapLinkPaste";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, ArrowRight, LocateFixed, Loader2 } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { GalleryManager } from "@/components/GalleryManager";
import { VenueMap } from "@/components/VenueMap";
import { useVenueCategories } from "@/hooks/useVenueCategories";
import { GOVERNORATES } from "@/pages/PartnerRegister";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

const FIELDS = ["name_ar", "name_en", "type", "governorate", "area", "area_en", "address", "hours_ar", "description_ar", "description_en", "lat", "lng", "gallery"];

export default function EditVenue() {
  const { id } = useParams();
  const { t, lang } = usePrefs();
  const cats = useVenueCategories();
  const [f, setF] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error(t("edit.gpsUnsupported"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      const lat = +pos.coords.latitude.toFixed(6), lng = +pos.coords.longitude.toFixed(6);
      setF((x: any) => ({ ...x, lat, lng }));
      setLocating(false);
      toast.success(t("pp.gpsPicked"));
    }, (err) => {
      setLocating(false);
      toast.error(t(err.code === 1 ? "edit.gpsDenied" : "edit.gpsFailed"));
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };
  const [review, setReview] = useState<{ status: string; reason?: string; edit?: string; editReason?: string }>({ status: "approved" });
  const toReview = (d: any) => ({ status: d.review_status || "approved", reason: d.review_reason, edit: d.edit_status, editReason: d.edit_reason });
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get(`/venue/venues/${id}`).then((r) => (setReview(toReview(r.data)), setF(Object.fromEntries(FIELDS.map((k) => [k, r.data[k] ?? (k === "gallery" ? [] : k === "lat" || k === "lng" ? null : "")]))))).catch((e) => toast.error(errorText(e, t)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (typeof f.lat !== "number" || typeof f.lng !== "number") return toast.error(t("spot.errLocation"));
    setBusy(true);
    try {
      const { data } = await api.patch(`/venue/venues/${id}`, f);
      setF(Object.fromEntries(FIELDS.map((k) => [k, data[k] ?? f[k]])));
      toast.success(t(review.status === "rejected" && data.review_status === "pending" ? "vr.resubmittedToast" : data.edit_status === "pending" ? "pp.editSent" : "edit.saved"));
      setReview(toReview(data));
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (!f) return <div className="h-64 rounded-3xl bg-muted animate-pulse" />;

  const text = (k: string, label: string, props: any = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`e-${k}`}>{t(label)}</Label>
      <Input id={`e-${k}`} value={f[k] ?? ""} onChange={(e: any) => set(k, props.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`edit-${k}-input`} className="h-11 rounded-2xl" {...props} />
    </div>
  );
  const area = (k: string, label: string, dir?: string) => (
    <div className="space-y-1.5 sm:col-span-2">
      <Label htmlFor={`e-${k}`}>{t(label)}</Label>
      <textarea id={`e-${k}`} dir={dir} rows={3} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`edit-${k}-input`} className="w-full rounded-2xl border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
    </div>
  );
  const card = "rounded-3xl border bg-card p-5 space-y-4";

  return (
    <form onSubmit={save} data-testid="edit-venue-page" className="space-y-5 max-w-3xl">
      <Link to="/venue" data-testid="edit-back-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-primary">
        <ArrowRight className="h-4 w-4 ltr:rotate-180" />
        {t("nav.myVenues")}
      </Link>
      <PageTitle
        title={t("edit.title")}
        sub={f.name_ar}
        action={review.status === "approved" &&
          <Link to={`/venues/${id}`} target="_blank" data-testid="edit-view-public-link" className="h-10 px-4 inline-flex items-center gap-1.5 rounded-full bg-accent text-sm font-bold">
            <ExternalLink className="h-4 w-4" />
            {t("edit.viewPublic")}
          </Link>
        }
      />
      {review.status === "pending" && <p data-testid="edit-review-note" className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-4 py-3 text-sm font-semibold">{t("vr.pendingNote")}</p>}
      {review.status === "approved" && review.edit === "pending" && <p data-testid="edit-pending-note" className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-4 py-3 text-sm font-semibold">{t("pp.editPending")}</p>}
      {review.status === "approved" && review.edit === "rejected" && <p data-testid="edit-rejected-note" className="rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 px-4 py-3 text-sm font-semibold">{t("pp.editRejected").replace("{r}", review.editReason || "")}</p>}
      {review.status === "approved" && !review.edit && <p data-testid="edit-approval-hint" className="rounded-2xl bg-accent px-4 py-3 text-sm">{t("pp.editHint")}</p>}
      {review.status === "rejected" && <p data-testid="edit-review-note" className="rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 px-4 py-3 text-sm font-semibold">{t("vr.rejectedNote").replace("{r}", review.reason || "")}</p>}
      <section className={card}>
        <h2 className="font-display text-lg font-bold">{t("edit.photos")}</h2>
        <GalleryManager value={f.gallery} onChange={(g) => set("gallery", g)} />
      </section>
      <section className={card}>
        <h2 className="font-display text-lg font-bold">{t("edit.details")}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {text("name_ar", "myVenues.nameAr", { required: true, minLength: 2 })}
          {text("name_en", "myVenues.nameEn", { dir: "ltr" })}
          <div className="space-y-1.5">
            <Label htmlFor="e-type">{t("myVenues.type")}</Label>
            <select id="e-type" required value={cats.some((c) => c.slug === f.type) ? f.type : ""} onChange={(e) => set("type", e.target.value)} data-testid="edit-type-select" className="h-11 w-full rounded-2xl border bg-background px-3 text-sm">
              {!cats.some((c) => c.slug === f.type) && <option value="" disabled>—</option>}
              {cats.map((c) => <option key={c.slug} value={c.slug}>{lang === "ar" ? c.name_ar : c.name_en}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="e-gov">{t("spot.governorate")}</Label>
            <select id="e-gov" required value={f.governorate || ""} onChange={(e) => set("governorate", e.target.value)} data-testid="edit-governorate-select" className="h-11 w-full rounded-2xl border bg-background px-3 text-sm">
              {!f.governorate && <option value="" disabled>—</option>}
              {GOVERNORATES.map((g) => <option key={g} value={g}>{t(`gov.${g}`)}</option>)}
            </select>
          </div>
          {text("area", "edit.area")}
          {text("area_en", "edit.areaEn", { dir: "ltr" })}
          {text("address", "edit.address")}
          {text("hours_ar", "spot.hours")}
          {area("description_ar", "myVenues.description")}
          {area("description_en", "edit.descEn", "ltr")}
        </div>
      </section>
      <section className={card}>
        <div>
          <h2 className="font-display text-lg font-bold">{t("edit.location")}</h2>
          <p className="text-sm text-muted-foreground">{t("edit.locationHint")}</p>
        </div>
        <button type="button" onClick={useMyLocation} disabled={locating} data-testid="edit-use-my-location"
          className="w-full h-12 rounded-full bg-foreground text-background font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
          {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" />}
          {t(locating ? "edit.gpsLocating" : "edit.gpsButton")}
        </button>
        <MapLinkPaste endpoint="/venue/maps/resolve" onPick={(lat, lng) => setF((x: any) => ({ ...x, lat, lng }))} />
        <VenueMap lat={f.lat} lng={f.lng} onPick={(lat, lng) => setF((x: any) => ({ ...x, lat, lng }))} className="h-72" />
        {typeof f.lat === "number" && <p data-testid="edit-coords" className="text-xs text-muted-foreground font-mono" dir="ltr">{f.lat}, {f.lng}</p>}
      </section>
      <div className="sticky bottom-24 md:bottom-4 z-10">
        <button disabled={busy} data-testid="edit-save-button" className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold shadow-xl shadow-primary/30 hover:bg-primary/90 disabled:opacity-60 transition-[background-color]">
          {busy ? t("common.loading") : t("common.save")}
        </button>
      </div>
    </form>
  );
}
